import { useState } from 'react';
import { useEffect } from 'react';
import { Modal, ModalBody } from 'reactstrap';

import ComponentCircleLoader from '../../components/Loaders/ComponentCircleLoader';
import { CommonImage } from '../../styledComponents/common';
import { getMapCircle, getMapPolygon } from '../../utils/helper';

const StaticTaskLocationModal = props => {
  const { isOpen, toggle, taskLocation } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    if (taskLocation && taskLocation.id) {
      setIsLoading(true);

      // Determine if this is a circular or polygonal geofence
      const isPolygonal = taskLocation.type === 'polygonal';

      // Modal dimensions
      const MODAL_WIDTH = 640; // pixels
      const MODAL_HEIGHT = 512; // pixels

      // Calculate appropriate zoom level based on the location type and size
      let zoomLevel = 16; // Default zoom level
      let center = null;

      if (isPolygonal && taskLocation.vertices && taskLocation.vertices.length >= 3) {
        // For polygon, calculate the bounding box to determine appropriate zoom
        let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;

        taskLocation.vertices.forEach(vertex => {
          minLat = Math.min(minLat, vertex[0]);
          maxLat = Math.max(maxLat, vertex[0]);
          minLng = Math.min(minLng, vertex[1]);
          maxLng = Math.max(maxLng, vertex[1]);
        });

        // Calculate the center of the polygon
        const centerLat = (minLat + maxLat) / 2;
        const centerLng = (minLng + maxLng) / 2;
        center = `${centerLat},${centerLng}`;

        // Calculate the size of the bounding box
        const latDiff = maxLat - minLat;
        const lngDiff = maxLng - minLng;

        // Calculate the aspect ratio of the bounding box
        const aspectRatio = latDiff / lngDiff;

        // Calculate the zoom level based on the size of the bounding box
        // We need to ensure the polygon fits within the modal dimensions
        // Google Maps uses a 256x256 tile at zoom level 0, and doubles for each zoom level

        // Calculate the zoom level needed to fit the width
        const zoomForWidth = Math.log2(360 * MODAL_WIDTH / (lngDiff * 256));

        // Calculate the zoom level needed to fit the height
        const zoomForHeight = Math.log2(180 * MODAL_HEIGHT / (latDiff * 256));

        // Use the smaller zoom level to ensure the polygon fits in both dimensions
        zoomLevel = Math.min(zoomForWidth, zoomForHeight);

        // Adjust zoom level based on aspect ratio
        // If the polygon is very wide or tall, we need to zoom out more
        if (aspectRatio > 1.5 || aspectRatio < 0.67) {
          // For polygons with extreme aspect ratios, zoom out more
          zoomLevel = Math.floor(zoomLevel) - 1;
        } else {
          // For more square-like polygons, we can zoom in more
          zoomLevel = Math.floor(zoomLevel) - 0.5;
        }

        // Clamp the zoom level between 12 and 20
        zoomLevel = Math.min(Math.max(zoomLevel, 12), 20);
      } else {
        // For circular geofence
        // Set center for circular geofence
        center = `${taskLocation.latitude},${taskLocation.longitude}`;

        // Calculate zoom level dynamically based on radius and modal dimensions
        // We need to ensure the circle fits within the modal dimensions

        // Constants for the calculation
        const PADDING_FACTOR = 1.1; // Reduced padding factor to zoom in more

        // Calculate the diameter of the circle in pixels at zoom level 0
        // At zoom level 0, the world map is 256x256 pixels
        // The circumference of the Earth is approximately 40,075 km
        // So 1 degree of longitude is approximately 111.32 km at the equator

        // Convert radius from meters to degrees
        const radiusInDegrees = taskLocation.radius / 111320; // meters to degrees

        // Calculate the diameter in degrees
        const diameterInDegrees = radiusInDegrees * 2 * PADDING_FACTOR;

        // Calculate the zoom level needed to fit the circle
        // We use the smaller dimension (height) to ensure the circle fits
        const zoomForCircle = Math.log2(180 * MODAL_HEIGHT / (diameterInDegrees * 256));

        // Round down and subtract less to zoom in more
        zoomLevel = Math.floor(zoomForCircle) - 0.5;

        // Clamp the zoom level between 12 and 20
        zoomLevel = Math.min(Math.max(zoomLevel, 12), 20);
      }

      // Build the base URL with the calculated zoom level and center
      let baseUrl = `https://maps.googleapis.com/maps/api/staticmap?key=${process.env.REACT_APP_GOOGLE_MAPS_JAVASCRIPT_API_KEY}&size=640x512&zoom=${zoomLevel}&scale=1&language=ar`;

      // Add center parameter if we have a center point
      if (center) {
        baseUrl += `&center=${center}`;
      }

      let markers = '';
      let path = '';

      if (isPolygonal && taskLocation.vertices && taskLocation.vertices.length >= 3) {
        // For polygonal geofence, use the first vertex as the marker location
        const firstVertex = taskLocation.vertices[0];
        markers = `&markers=color:red%7C${firstVertex[0]},${firstVertex[1]}`;

        // Generate polygon path
        const polygonPath = getMapPolygon(taskLocation.vertices);
        path = `&path=fillcolor:0x6f7aff33%7Cweight:2%7Ccolor:0x6f7affff${polygonPath}`;
      } else {
        // For circular geofence
        markers = `&markers=color:red%7C${taskLocation.latitude},${taskLocation.longitude}`;
        path = `&path=fillcolor:0x6f7aff33%7Cweight:2%7Ccolor:0x6f7affff${getMapCircle(
          taskLocation.latitude,
          taskLocation.longitude,
          taskLocation.radius,
        )}`;
      }

      const url = `${baseUrl}${markers}${path}`;
      setImageUrl(url);
      setIsLoading(false);
    }
  }, [taskLocation]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      style={{ maxWidth: '640px', maxHeight: '512px' }}
    >
      <ModalBody style={{ padding: `10px` }}>
        {isLoading ? (
          <ComponentCircleLoader />
        ) : (
          <CommonImage src={imageUrl} alt="map" />
        )}
      </ModalBody>
    </Modal>
  );
};

export default StaticTaskLocationModal;
